import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SelectField } from "@/components/common";
import { OrgTreeSelect } from "@/components/common/OrgTreeSelect";
import { useTran } from "@/hooks";
import { useAuthStore } from "@/features/auth";
import { formatReportingYearPeriod } from "@/features/esg/utils/period-constants";
import { useDashboardFiltersStore } from "../hooks/useDashboardStore";
import {
  ALL,
  arrayToSingle,
  buildCategoryOptions,
  buildHierarchyOptions,
  buildOrganizationPickerTree,
  collectPickerOptionIds,
  getScopedReportingYearOrganizationIds,
  singleToArray,
  scopeOrgTreeByReportingYears,
} from "../utils/dashboard-filter-options";
import {
  buildReportingFrameworkOptions,
  sortReportingYearsDescending,
} from "../utils/reporting-years";

const JJ_GROUP_FRAMEWORK = "JJ-Group";
const FILTER_CONTROL_CLASS = "w-full sm:w-[201px]";

const FilterBar = observer(({ hideFilterBy = false }) => {
  const { t } = useTran();
  const { filters, available, setFilter, reset, activeCount } =
    useDashboardFiltersStore();
  const { user, isJJSEA } = useAuthStore();

  const yearOptions = useMemo(() => {
    const reportingYearOptions = sortReportingYearsDescending(
      available.reportingYears,
    ).map((reportingYear) => ({
      value: String(reportingYear.startYear),
      label: formatReportingYearPeriod(reportingYear),
    }));

    if (reportingYearOptions.length > 0) {
      if (reportingYearOptions.length === 1) return reportingYearOptions;
      return [{ value: ALL, label: t("All Years") }, ...reportingYearOptions];
    }

    const fallbackYearOptions = available.years
      .slice()
      .sort((a, b) => b - a)
      .map((y) => ({ value: String(y), label: String(y) }));

    if (fallbackYearOptions.length === 1) return fallbackYearOptions;

    return [{ value: ALL, label: t("All Years") }, ...fallbackYearOptions];
  }, [available.reportingYears, available.years, t]);

  const frameworkOptions = useMemo(() => {
    return buildReportingFrameworkOptions({
      reportingYears: available.reportingYears,
      frameworkObjects: available.frameworkObjects,
      fallbackFrameworks: available.frameworks,
      selectedYears: filters.years,
      isJJCompany: isJJSEA,
      jjGroupFramework: JJ_GROUP_FRAMEWORK,
      allValue: ALL,
      allLabel: t("All Frameworks"),
      includeAllOption: false,
    });
  }, [
    available.frameworkObjects,
    available.frameworks,
    available.reportingYears,
    filters.years,
    isJJSEA,
    t,
  ]);

  const categoryOptions = useMemo(
    () => buildCategoryOptions({ user, t }),
    [user, t],
  );

  const scopedOrgTree = useMemo(
    () =>
      scopeOrgTreeByReportingYears({
        orgTree: available.orgTree,
        reportingYears: available.reportingYears,
        selectedYears: filters.years,
        companyId: user?.companyId,
      }),
    [
      available.orgTree,
      available.reportingYears,
      filters.years,
      user?.companyId,
    ],
  );
  const selectableOrgIds = useMemo(
    () =>
      getScopedReportingYearOrganizationIds({
        reportingYears: available.reportingYears,
        selectedYears: filters.years,
        companyId: user?.companyId,
      }),
    [available.reportingYears, filters.years, user?.companyId],
  );

  const hierarchyOptions = useMemo(
    () => buildHierarchyOptions(scopedOrgTree, t),
    [scopedOrgTree, t],
  );

  useEffect(() => {
    if (hideFilterBy) return;
    if (!hierarchyOptions.some((o) => o.value === filters.hierarchy)) {
      setFilter("hierarchy", "all");
    }
  }, [hideFilterBy, hierarchyOptions, filters.hierarchy, setFilter]);

  useEffect(() => {
    const realYearOptions = yearOptions.filter(
      (option) => option.value !== ALL,
    );
    if (realYearOptions.length === 1) {
      const onlyYear = Number(realYearOptions[0].value);
      if (filters.years.length !== 1 || filters.years[0] !== onlyYear) {
        setFilter("years", [onlyYear]);
      }
      return;
    }

    if (filters.years.length === 0) return;

    const availableYears = new Set(
      realYearOptions.map((option) => option.value),
    );
    const selectedYears = filters.years.map(String);
    if (!selectedYears.every((year) => availableYears.has(year))) {
      setFilter("years", []);
    }
  }, [filters.years, setFilter, yearOptions]);

  useEffect(() => {
    const realFrameworkOptions = frameworkOptions.filter(
      (option) => option.value !== ALL,
    );

    if (realFrameworkOptions.length === 0) {
      if (filters.frameworks.length > 0) setFilter("frameworks", []);
      return;
    }

    const availableFrameworks = new Set(
      realFrameworkOptions.map((option) => option.value),
    );
    if (
      filters.frameworks.length === 1 &&
      availableFrameworks.has(filters.frameworks[0])
    ) {
      return;
    }

    setFilter("frameworks", [realFrameworkOptions[0].value]);
  }, [filters.frameworks, frameworkOptions, setFilter]);

  const pickerTree = useMemo(
    () =>
      buildOrganizationPickerTree(
        scopedOrgTree,
        filters.hierarchy,
        selectableOrgIds,
      ),
    [filters.hierarchy, scopedOrgTree, selectableOrgIds],
  );
  const allowedPickerOrgIds = useMemo(
    () => (filters.hierarchy === "all" ? selectableOrgIds || [] : []),
    [filters.hierarchy, selectableOrgIds],
  );

  useEffect(() => {
    if (filters.orgIds.length === 0) return;

    const availableOrgIds = new Set(collectPickerOptionIds(pickerTree));
    const selectableOrgIdSet =
      Array.isArray(allowedPickerOrgIds) && allowedPickerOrgIds.length > 0
        ? new Set(allowedPickerOrgIds.map(String))
        : null;
    if (
      filters.orgIds.every((id) => {
        const orgId = String(id);
        return (
          availableOrgIds.has(orgId) &&
          (!selectableOrgIdSet || selectableOrgIdSet.has(orgId))
        );
      })
    ) {
      return;
    }

    setFilter("orgIds", []);
  }, [allowedPickerOrgIds, filters.orgIds, pickerTree, setFilter]);

  const selectedYear = arrayToSingle(filters.years.map(String));
  const selectedFramework = filters.frameworks[0] || "";

  // The Clear badge counts only clearable choices. Framework (and year, when
  // the boundary offers a single year) is force-selected by the effects
  // above whenever it has exactly one option, so that selection is not
  // something Clear can undo and does not count as an active filter.
  const realYearOptions = yearOptions.filter((option) => option.value !== ALL);
  const realFrameworkOptions = frameworkOptions.filter(
    (option) => option.value !== ALL,
  );
  const yearForced =
    realYearOptions.length === 1 &&
    filters.years.length === 1 &&
    String(filters.years[0]) === realYearOptions[0].value;
  const frameworkForced =
    realFrameworkOptions.length === 1 &&
    filters.frameworks.length === 1 &&
    filters.frameworks[0] === realFrameworkOptions[0].value;
  const clearableCount =
    activeCount - (yearForced ? 1 : 0) - (frameworkForced ? 1 : 0);

  return (
    <div className="flex flex-wrap items-end gap-3 bg-muted/40 rounded-lg p-3">
      <SelectField
        label={t("Reporting Year")}
        options={yearOptions}
        value={selectedYear}
        onValueChange={(value) =>
          setFilter("years", singleToArray(value).map(Number))
        }
        sortOptions={false}
        className={FILTER_CONTROL_CLASS}
        contentClassName="w-[min(17rem,calc(100vw-1rem))]"
      />
      {!hideFilterBy && (
        <SelectField
          label={t("Filter By")}
          options={hierarchyOptions}
          value={filters.hierarchy}
          onValueChange={(v) => setFilter("hierarchy", v)}
          sortOptions={false}
          className={FILTER_CONTROL_CLASS}
        />
      )}

      <div className={`${FILTER_CONTROL_CLASS} space-y-1.5`}>
        <label className="text-sm font-medium text-foreground">
          {t("Organization")}
        </label>
        <OrgTreeSelect
          tree={pickerTree}
          value={filters.orgIds}
          onChange={(ids) => setFilter("orgIds", ids)}
          multiSelect
          showBranchSelectAll
          branchSelectLevels={["parent_company", "level_1"]}
          placeholder={t("All Organizations")}
          className="w-full"
          allowOrgs={allowedPickerOrgIds}
        />
      </div>
      <SelectField
        label={t("Framework")}
        options={frameworkOptions}
        value={selectedFramework}
        onValueChange={(value) => setFilter("frameworks", value ? [value] : [])}
        sortOptions={false}
        className={FILTER_CONTROL_CLASS}
      />
      <SelectField
        label={t("Category")}
        options={categoryOptions}
        value={arrayToSingle(filters.categories)}
        onValueChange={(v) => setFilter("categories", singleToArray(v))}
        sortOptions={false}
        className={FILTER_CONTROL_CLASS}
      />

      {clearableCount > 0 && (
        <Button variant="outline" size="sm" onClick={reset} className="gap-1.5">
          <X size={14} />
          {t("Clear")}
          <Badge
            variant="secondary"
            className="ml-1 px-1.5 py-0 text-[10px] font-semibold"
          >
            {clearableCount}
          </Badge>
        </Button>
      )}
    </div>
  );
});

export default FilterBar;
